import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface CampoImportavel {
  chave: string;
  label: string;
  obrigatorio: boolean;
  tipo: string;
}

interface PreviewResposta {
  colunas: string[];
  amostra: Record<string, string>[];
  campos: CampoImportavel[];
  sugestoesMapeamento: Record<string, string>;
}

interface ErroLinha {
  linha: number;
  motivo: string;
}

interface ExecucaoResposta {
  total: number;
  sucesso: number;
  erros: ErroLinha[];
}

interface Turma {
  id: number;
  nome: string;
}

const ENTIDADES_DISPONIVEIS = [{ chave: 'alunos', label: 'Alunos' }];

export default function ImportacaoPage() {
  const [entidade, setEntidade] = useState('alunos');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResposta | null>(null);
  const [mapeamento, setMapeamento] = useState<Record<string, string>>({});
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [turmaPadraoId, setTurmaPadraoId] = useState<string>('');
  const [carregando, setCarregando] = useState(false);
  const [resultado, setResultado] = useState<ExecucaoResposta | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get<Turma[]>('/turmas').then(({ data }) => setTurmas(data)).catch(() => setTurmas([]));
  }, []);

  function reiniciar() {
    setArquivo(null);
    setPreview(null);
    setMapeamento({});
    setTurmaPadraoId('');
    setResultado(null);
    setErro('');
  }

  async function enviarPreview() {
    if (!arquivo) {
      setErro('Selecione um arquivo .xlsx ou .csv.');
      return;
    }
    setErro('');
    setCarregando(true);
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      const { data } = await api.post<PreviewResposta>(`/importacao/${entidade}/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
      setMapeamento(data.sugestoesMapeamento || {});
      setResultado(null);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível ler o arquivo.');
    } finally {
      setCarregando(false);
    }
  }

  async function confirmarImportacao() {
    if (!arquivo || !preview) return;
    setErro('');
    setCarregando(true);
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      formData.append('mapeamento', JSON.stringify(mapeamento));
      if (turmaPadraoId) formData.append('turma_padrao_id', turmaPadraoId);
      const { data } = await api.post<ExecucaoResposta>(`/importacao/${entidade}/executar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResultado(data);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível concluir a importação.');
    } finally {
      setCarregando(false);
    }
  }

  const camposObrigatoriosFaltando = preview
    ? preview.campos.filter((c) => c.obrigatorio && !mapeamento[c.chave])
    : [];

  return (
    <div>
      <h2 className="pagina-titulo">Importar Dados</h2>
      <p className="pagina-subtitulo">
        Envie uma planilha (.xlsx ou .csv) e mapeie as colunas para os campos do sistema. Nenhum dado é
        importado até você confirmar o mapeamento.
      </p>

      {erro && <p style={{ color: '#D32F2F', fontWeight: 'bold' }}>{erro}</p>}

      {!resultado && (
        <div style={{ marginBottom: 20 }}>
          <label>
            Tipo de dado a importar
            <select value={entidade} onChange={(e) => { setEntidade(e.target.value); reiniciar(); }} style={{ display: 'block', marginTop: 4 }}>
              {ENTIDADES_DISPONIVEIS.map((opt) => (
                <option key={opt.chave} value={opt.chave}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {!preview && !resultado && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="file"
            accept=".xlsx,.csv"
            onChange={(e) => setArquivo(e.target.files?.[0] || null)}
          />
          <button onClick={enviarPreview} disabled={carregando || !arquivo}>
            {carregando ? 'Lendo...' : 'Continuar'}
          </button>
        </div>
      )}

      {preview && !resultado && (
        <div>
          <h3>Mapeamento de campos</h3>
          <p className="texto-ajuda">Para cada campo do sistema, escolha qual coluna da planilha corresponde a ele.</p>

          <table className="tabela" style={{ marginBottom: 20 }}>
            <thead>
              <tr>
                <th>Campo do sistema</th>
                <th>Coluna na planilha</th>
              </tr>
            </thead>
            <tbody>
              {preview.campos.map((campo) => (
                <tr key={campo.chave}>
                  <td>
                    {campo.label}
                    {campo.obrigatorio && <span style={{ color: '#D32F2F' }}> *</span>}
                  </td>
                  <td>
                    <select
                      value={mapeamento[campo.chave] || ''}
                      onChange={(e) => setMapeamento((m) => ({ ...m, [campo.chave]: e.target.value }))}
                    >
                      <option value="">Não importar</option>
                      {preview.colunas.map((coluna) => (
                        <option key={coluna} value={coluna}>{coluna}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {entidade === 'alunos' && (
            <label style={{ display: 'block', marginBottom: 20 }}>
              Turma padrão (usada quando a linha não tiver turma ou a turma não for encontrada)
              <select value={turmaPadraoId} onChange={(e) => setTurmaPadraoId(e.target.value)} style={{ display: 'block', marginTop: 4 }}>
                <option value="">Nenhuma</option>
                {turmas.map((t) => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
            </label>
          )}

          <h3>Amostra (primeiras linhas)</h3>
          <div style={{ overflowX: 'auto', marginBottom: 20 }}>
            <table className="tabela">
              <thead>
                <tr>
                  {preview.colunas.map((c) => <th key={c}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.amostra.map((linha, idx) => (
                  <tr key={idx}>
                    {preview.colunas.map((c) => <td key={c}>{linha[c]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {camposObrigatoriosFaltando.length > 0 && (
            <p style={{ color: '#D32F2F' }}>
              Mapeie os campos obrigatórios antes de continuar: {camposObrigatoriosFaltando.map((c) => c.label).join(', ')}.
            </p>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={reiniciar}>Voltar</button>
            <button
              onClick={confirmarImportacao}
              disabled={carregando || camposObrigatoriosFaltando.length > 0}
            >
              {carregando ? 'Importando...' : 'Confirmar importação'}
            </button>
          </div>
        </div>
      )}

      {resultado && (
        <div>
          <h3>Resultado da importação</h3>
          <p>
            <strong>{resultado.sucesso}</strong> de <strong>{resultado.total}</strong> registros importados com sucesso.
          </p>
          {resultado.erros.length > 0 && (
            <>
              <p style={{ color: '#D32F2F', fontWeight: 'bold' }}>{resultado.erros.length} linha(s) com erro:</p>
              <ul>
                {resultado.erros.map((e) => (
                  <li key={e.linha}>Linha {e.linha}: {e.motivo}</li>
                ))}
              </ul>
            </>
          )}
          <button onClick={reiniciar} style={{ marginTop: 10 }}>Importar outro arquivo</button>
        </div>
      )}
    </div>
  );
}
